import { Alert, Button, Card, Col, Divider, Drawer, Empty, Row, Segmented, Space, Spin, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { fetchCustomerProfile } from "../../../apis/call_intelligence.api";
import {
  ago,
  CallDetailPanel,
  direction,
  dur,
  fmtD,
  fmtDT,
  FlagTag,
  flagReason,
  inr,
  mins,
  minsSince,
  orderStatus,
  SENTIMENT_COLOR,
  SENTIMENT_WORD,
  sentimentTag,
} from "./shared";

const { Text, Title, Paragraph } = Typography;

const H48 = 48 * 60;

// Whole IST days between an order and an event on it ("Day 3").
const dayOf = (orders: any[], enq: string, iso: string) => {
  const o = orders.find((x) => x.enq === enq);
  if (!o) return null;
  return dayjs(iso).startOf("day").diff(dayjs(o.placed_at).startOf("day"), "day");
};
const promisedPassed = (o: any) =>
  o.promised_date && o.status !== "delivered" && dayjs(o.promised_date).endOf("day").isBefore(dayjs());

const TYPE_COLOR: Record<string, string> = { Order: "blue", Ticket: "purple", Call: "cyan" };
const FRT_TARGET_MINS = 120;

const median = (xs: number[]) => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

const topCounts = (values: string[]) => {
  const c: Record<string, number> = {};
  values
    .flatMap((v) => (v || "").split(", "))
    .filter(Boolean)
    .forEach((v) => (c[v] = (c[v] || 0) + 1));
  return Object.entries(c).sort((a, b) => b[1] - a[1]);
};

// Ticket stats for one customer: response times, repeat issues, reasons, root causes.
const TicketAnalysis = ({ tickets, orders }: { tickets: any[]; orders: any[] }) => {
  if (!tickets.length) return null;
  const frts = tickets.map((t) => t.frt_mins).filter((v) => v != null);
  const rts = tickets.map((t) => t.rt_mins).filter((v) => v != null);
  const late = frts.filter((v) => v > FRT_TARGET_MINS).length;
  const noCall = tickets.filter((t) => t.active && t.frt_mins == null).length;
  const perOrder = topCounts(tickets.map((t) => t.enq)).filter(([, n]) => n > 1);
  const reasons = topCounts(tickets.map((t) => t.raise_reason));
  const rcas = topCounts(tickets.map((t) => t.rca));
  const attributions = topCounts(tickets.map((t) => t.attribution));
  const medFrt = median(frts);
  const medRt = median(rts);
  const stat = (label: string, value: any, warn = false) => (
    <Col xs={12} md={4}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {label}
      </Text>
      <div style={{ fontSize: 18, fontWeight: 600, color: warn ? "#cf1322" : undefined }}>{value}</div>
    </Col>
  );
  const chips = (rows: [string, number][]) =>
    rows.length ? (
      rows.slice(0, 5).map(([k, n]) => (
        <Tag key={k}>
          {k} × {n}
        </Tag>
      ))
    ) : (
      <Text type="secondary">not set</Text>
    );

  return (
    <Card size="small" title="Ticket analysis" style={{ marginBottom: 12 }}>
      <Row gutter={[12, 12]}>
        {stat("Tickets", `${tickets.length} (${tickets.filter((t) => t.escalation).length} escalation)`)}
        {stat("Median first call", medFrt != null ? mins(medFrt) : "-", medFrt != null && medFrt > FRT_TARGET_MINS)}
        {stat("First call over 2 h", `${late} of ${frts.length}`, late > 0)}
        {stat("Active, no call yet", noCall, noCall > 0)}
        {stat("Median resolution", medRt != null ? mins(medRt) : "-")}
        {stat("Orders with 2+ tickets", `${perOrder.length} of ${orders.length}`, perOrder.length > 0)}
      </Row>
      <Divider style={{ margin: "10px 0" }} />
      <Space direction="vertical" size={6} style={{ width: "100%" }}>
        <div>
          <Text type="secondary">Raised for: </Text>
          {chips(reasons)}
        </div>
        <div>
          <Text type="secondary">Root cause (RCA): </Text>
          {chips(rcas)}
        </div>
        <div>
          <Text type="secondary">Attribution: </Text>
          {chips(attributions)}
        </div>
        {perOrder.length > 0 && (
          <div>
            <Text type="secondary">Repeat tickets on: </Text>
            {perOrder.map(([enq, n]) => (
              <Tag key={enq} color="orange">
                {enq} × {n}
              </Tag>
            ))}
          </div>
        )}
      </Space>
    </Card>
  );
};

const CustomerProfile = ({ userId, onBack }: { userId: string; onBack: () => void }) => {
  const [p, setP] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [evFilter, setEvFilter] = useState<string>("all");
  const [drawerCall, setDrawerCall] = useState<number | null>(null);

  useEffect(() => {
    setLoading(true);
    fetchCustomerProfile(userId).then((res) => {
      setP(res);
      setLoading(false);
    });
  }, [userId]);

  const events = useMemo(() => {
    if (!p) return [];
    const ev: any[] = [];
    p.orders.forEach((o: any) =>
      ev.push({
        at: o.placed_at,
        type: "Order",
        ref: o.enq,
        text: `Order placed · ${o.category || "—"} · ${inr(o.value)}`,
        who: "",
        status: orderStatus(o.status),
        day: 0,
      })
    );
    p.tickets.forEach((t: any) => {
      if (!t.created_at) return;
      ev.push({
        at: t.created_at,
        type: "Ticket",
        ref: `#${t.id}${t.enq ? ` · ${t.enq}` : ""}`,
        text:
          `Ticket raised: ${t.raise_reason || "no raise request"} (${t.request_type || "request type not set"})` +
          (t.attribution ? ` · attribution ${t.attribution}` : "") +
          (t.rca ? ` · ${t.rca}` : ""),
        who: t.owner,
        status: t.stage,
        day: dayOf(p.orders, t.enq, t.created_at),
      });
      if (t.frt_mins != null) {
        const at = dayjs(t.created_at).add(t.frt_mins, "minute").toISOString();
        ev.push({
          at,
          type: "Call",
          ref: `#${t.id}`,
          text: `First call on ticket, ${mins(t.frt_mins)} after it was raised (from the ticket's first-call time)`,
          who: t.owner,
          status: t.frt_mins > 120 ? "Late (over 2 h)" : "Within 2 h",
          day: dayOf(p.orders, t.enq, at),
        });
      }
      if (t.rt_mins != null) {
        const at = dayjs(t.created_at).add(t.rt_mins, "minute").toISOString();
        ev.push({
          at,
          type: "Ticket",
          ref: `#${t.id}`,
          text: `Ticket resolved after ${mins(t.rt_mins)}`,
          who: t.owner,
          status: "Resolved",
          day: dayOf(p.orders, t.enq, at),
        });
      }
    });
    p.calls.forEach((c: any) =>
      ev.push({
        at: c.start,
        type: "Call",
        ref: `#${c.id}`,
        text: `${direction(c.call_type)} call, ${dur(c.duration)}. ${(c.summary || "").slice(0, 110)}${
          (c.summary || "").length > 110 ? "…" : ""
        }`,
        who: c.agent_name,
        call: c,
      })
    );
    return ev
      .filter((e) => e.at)
      .sort((a, b) => dayjs(b.at).valueOf() - dayjs(a.at).valueOf())
      .map((e, i) => ({ ...e, key: i }));
  }, [p]);

  if (loading)
    return (
      <div style={{ textAlign: "center", padding: 60 }}>
        <Spin />
      </div>
    );
  if (!p)
    return (
      <div>
        <Button onClick={onBack}>← Back to the customer list</Button>
        <Empty description="Customer not found" />
      </div>
    );

  const spend = p.orders.reduce((s: number, o: any) => s + o.value, 0);
  const lastOrder = p.orders[0];
  const activeEsc = p.tickets.filter((t: any) => t.active && t.escalation);
  const activeSup = p.tickets.filter((t: any) => t.active && !t.escalation);
  const latestTicket = p.tickets[0];
  const scoredCalls = p.calls.filter((c: any) => c.sentiment);
  const tags: string[] = [];
  if (p.orders.length >= 3) tags.push("Repeat buyer");
  if (p.tickets.length >= 5) tags.push(`Many tickets (${p.tickets.length})`);
  if (p.tickets.filter((t: any) => t.escalation).length >= 3) tags.push("Repeat escalations");

  const newestCallId = p.calls[0]?.id;
  const shown = events.filter((e) => evFilter === "all" || e.type === evFilter);

  const timelineCols: ColumnsType<any> = [
    { title: "Date and time", key: "at", width: 140, render: (e) => fmtDT(e.at) },
    { title: "Event", key: "type", width: 80, render: (e) => <Tag color={TYPE_COLOR[e.type]}>{e.type}</Tag> },
    { title: "Reference", dataIndex: "ref", key: "ref", width: 190 },
    { title: "What happened", dataIndex: "text", key: "text" },
    {
      title: "Day since order",
      key: "day",
      width: 110,
      render: (e) => (e.day == null ? "" : `Day ${e.day}`),
    },
    { title: "Agent or owner", dataIndex: "who", key: "who", width: 150 },
    {
      title: "Status / sentiment",
      key: "status",
      width: 170,
      render: (e) => (e.call ? sentimentTag(e.call.sentiment, e.call.severity) : e.status),
    },
  ];

  const orderCols: ColumnsType<any> = [
    { title: "Order (ENQ)", dataIndex: "enq", key: "enq" },
    { title: "Placed", key: "placed", render: (o) => fmtDT(o.placed_at) },
    {
      title: "Promised",
      key: "promised",
      render: (o) => (
        <span>
          {o.promised_date ? fmtD(o.promised_date) : "-"}{" "}
          {promisedPassed(o) && <Tag color="orange">passed</Tag>}
        </span>
      ),
    },
    { title: "Value", key: "value", render: (o) => inr(o.value) },
    { title: "City and category", key: "cc", render: (o) => [o.city, o.category].filter(Boolean).join(" · ") },
    {
      title: "Tickets",
      key: "tickets",
      render: (o) => {
        const ts = p.tickets.filter((t: any) => t.enq === o.enq);
        return (
          <span>
            {ts.length} <Text type="secondary">({ts.filter((t: any) => t.escalation).length} escalation)</Text>
          </span>
        );
      },
    },
    {
      title: "First ticket",
      key: "first",
      render: (o) => {
        const ts = p.tickets
          .filter((t: any) => t.enq === o.enq && t.created_at)
          .sort((a: any, b: any) => dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf());
        return ts.length ? `Day ${dayOf(p.orders, o.enq, ts[0].created_at)}` : <Text type="secondary">none</Text>;
      },
    },
    { title: "Status", key: "status", render: (o) => <Tag>{orderStatus(o.status)}</Tag> },
  ];

  return (
    <div>
      <Button onClick={onBack} style={{ marginBottom: 12 }}>
        ← Back to the customer list
      </Button>

      <Card style={{ marginBottom: 12 }}>
        <Row justify="space-between" gutter={16}>
          <Col>
            <Title level={4} style={{ margin: 0 }}>
              {p.name || "Unnamed customer"}
            </Title>
            <Text type="secondary">
              {p.phone || "Phone not linked"}
              {p.city ? ` · ${p.city}` : ""}
              {p.orders.length ? ` · first order ${fmtD(p.orders[p.orders.length - 1].placed_at)}` : ""}
            </Text>
            <div style={{ marginTop: 6 }}>
              {tags.map((t) => (
                <Tag key={t}>{t}</Tag>
              ))}
            </div>
          </Col>
          <Col style={{ textAlign: "right" }}>
            <div>
              <Text type="secondary">
                {p.orders.length} {p.orders.length === 1 ? "order" : "orders"} · {inr(spend)}
              </Text>
            </div>
            {latestTicket?.owner && (
              <div>
                <Text type="secondary">CS owner on latest ticket: {latestTicket.owner}</Text>
              </div>
            )}
            <FlagTag flagged={p.flagged} sentiment={p.latest_conversation_sentiment} />
          </Col>
        </Row>
      </Card>

      <Card size="small" title="Overall call summary" style={{ marginBottom: 12 }}>
        {scoredCalls.length ? (
          <>
            <Space wrap style={{ marginBottom: 6 }}>
              <Text type="secondary">Latest sentiment</Text>
              {sentimentTag(scoredCalls[0].sentiment, scoredCalls[0].severity)}
              <Text type="secondary">
                {scoredCalls.length} {scoredCalls.length === 1 ? "call" : "calls"}, oldest to newest
              </Text>
              {scoredCalls
                .slice(0, 10)
                .reverse()
                .map((c: any) => (
                  <Tag key={c.id} color={SENTIMENT_COLOR[c.sentiment]} title={fmtDT(c.start)}>
                    {SENTIMENT_WORD[c.sentiment] || c.sentiment}
                  </Tag>
                ))}
            </Space>
            <Paragraph style={{ margin: 0 }}>
              {p.overall_summary?.summary || (
                <Text type="secondary">The overall summary is written hourly after new calls are scored.</Text>
              )}
            </Paragraph>
          </>
        ) : (
          <Text type="secondary">No calls linked to this customer in the call records.</Text>
        )}
      </Card>

      {p.flagged && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            <span>
              <Text strong>Flagged:</Text> {flagReason(p)}
            </span>
          }
          description="Rule: negative conversation in the last 7 days and no call update for 1 hour. A call update is any call, including a ticket's first call."
        />
      )}

      <Title level={5}>Recent</Title>
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24} md={12} xl={6}>
          <Card size="small" title="Last order placed" style={{ height: "100%" }}>
            {lastOrder ? (
              <Space direction="vertical" size={2}>
                <span>
                  <Text strong>{lastOrder.enq}</Text> <Tag>{orderStatus(lastOrder.status)}</Tag>
                </span>
                <span>
                  Placed {fmtDT(lastOrder.placed_at)} <Text type="secondary">({ago(lastOrder.placed_at)})</Text>
                </span>
                <span>
                  {inr(lastOrder.value)} · {[lastOrder.city, lastOrder.category].filter(Boolean).join(" · ")} (
                  {lastOrder.item_count} {lastOrder.item_count === 1 ? "item" : "items"})
                </span>
                <span>
                  Promised {lastOrder.promised_date ? fmtD(lastOrder.promised_date) : "-"}{" "}
                  {promisedPassed(lastOrder) && <Tag color="orange">date passed, not delivered</Tag>}
                </span>
              </Space>
            ) : (
              <Text type="secondary">No orders</Text>
            )}
          </Card>
        </Col>

        <Col xs={24} md={12} xl={6}>
          <Card
            size="small"
            title={
              <span>
                Active escalation tickets <Tag>{activeEsc.length}</Tag>
              </span>
            }
            style={{ height: "100%" }}
          >
            {activeEsc.length ? (
              activeEsc.map((t: any) => (
                <div key={t.id} style={{ marginBottom: 8 }}>
                  <Text strong>#{t.id}</Text> · {t.raise_reason || "Escalation"}
                  <div>
                    <Text type="secondary">
                      {[t.enq, t.stage, t.owner, `age ${mins(minsSince(t.created_at))}`].filter(Boolean).join(" · ")}
                    </Text>
                  </div>
                  <div>
                    <Text type="secondary">
                      {t.rca ? `RCA ${t.rca} · ` : ""}
                      {t.frt_mins != null ? `first call after ${mins(t.frt_mins)}` : ""}
                    </Text>
                    {t.frt_mins == null && <Text strong>no call yet</Text>}
                  </div>
                </div>
              ))
            ) : (
              <Text type="secondary">No active escalations</Text>
            )}
            <Divider style={{ margin: "8px 0" }} />
            {activeSup.length ? (
              <>
                <Text strong>
                  {activeSup.length} active support {activeSup.length > 1 ? "tickets" : "ticket"}
                </Text>
                {activeSup.map((t: any) => (
                  <div key={t.id}>
                    <Text type="secondary">
                      #{t.id} · {t.raise_reason || "Support"} · {t.stage} ·{" "}
                      {t.frt_mins != null ? `first call after ${mins(t.frt_mins)}` : "no call yet"}
                    </Text>
                  </div>
                ))}
              </>
            ) : (
              <Text type="secondary">No active support tickets</Text>
            )}
          </Card>
        </Col>

        <Col xs={24} md={12} xl={6}>
          <Card size="small" title="Last 2 calls" style={{ height: "100%" }}>
            {p.calls.length ? (
              p.calls.slice(0, 2).map((c: any, i: number) => (
                <div key={c.id}>
                  {i > 0 && <Divider style={{ margin: "8px 0" }} />}
                  <Text strong>{fmtDT(c.start)}</Text> · {direction(c.call_type)} · {dur(c.duration)}
                  {c.agent_name && (
                    <div>
                      <Text type="secondary">{c.agent_name}</Text>
                    </div>
                  )}
                  <div>{sentimentTag(c.sentiment, c.severity)}</div>
                  <Paragraph ellipsis={{ rows: 3 }} style={{ margin: "4px 0" }}>
                    {c.summary}
                  </Paragraph>
                  <Button size="small" onClick={() => setDrawerCall(c.id)}>
                    Summary and transcript
                  </Button>
                </div>
              ))
            ) : (
              <Text type="secondary">No calls linked to this customer</Text>
            )}
          </Card>
        </Col>

        <Col xs={24} md={12} xl={6}>
          <Card size="small" title="WhatsApp sentiment" style={{ height: "100%" }}>
            <Tag>Not connected</Tag>
            <div style={{ marginTop: 6 }}>
              <Text type="secondary">
                Once WhatsApp conversations are fed in, this shows the label, score, 7-day trend, message count and
                who owes the reply.
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      <TicketAnalysis tickets={p.tickets} orders={p.orders} />

      <Card size="small" title="Event timeline" style={{ marginBottom: 12 }}>
        <Space wrap style={{ marginBottom: 8 }}>
          <Segmented
            value={evFilter}
            onChange={(v) => setEvFilter(v as string)}
            options={[
              { value: "all", label: `All (${events.length})` },
              { value: "Order", label: "Order" },
              { value: "Ticket", label: "Ticket" },
              { value: "Call", label: "Call" },
            ]}
          />
          <Tag>WhatsApp: not connected</Tag>
        </Space>
        <Table
          rowKey="key"
          size="small"
          columns={timelineCols}
          dataSource={shown}
          pagination={{ pageSize: 25, hideOnSinglePage: true }}
          scroll={{ x: 1100 }}
          onRow={(e) => ({ style: minsSince(e.at) <= H48 ? { background: "#fffbe6" } : {} })}
          expandable={{
            rowExpandable: (e) => !!e.call,
            expandedRowRender: (e) => <CallDetailPanel callId={e.call.id} />,
            defaultExpandedRowKeys: shown.filter((e) => e.call?.id === newestCallId).map((e) => e.key),
          }}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          Newest first. Rows from the last 48 hours are highlighted. Expand a call to see its summary and transcript.
        </Text>
      </Card>

      <Card size="small" title="Order history">
        <Table
          rowKey="enq"
          size="small"
          columns={orderCols}
          dataSource={p.orders}
          pagination={false}
          scroll={{ x: 900 }}
        />
      </Card>

      <Drawer
        title={drawerCall ? `Call #${drawerCall}` : ""}
        width={900}
        open={drawerCall != null}
        onClose={() => setDrawerCall(null)}
        destroyOnClose
      >
        {drawerCall != null && <CallDetailPanel callId={drawerCall} />}
      </Drawer>
    </div>
  );
};

export default CustomerProfile;
